import { Component, DestroyRef, ElementRef, HostListener, OnInit, ViewChild, inject } from '@angular/core';
import { Popover, PopoverModule } from 'primeng/popover';
import {NovaWorkComponent} from './nova-work.component';
import {NovaActivityService} from '../services/nova-activity.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ConversationLoadResult, MessagesContainerComponent } from "../ai-chatbot/messages-container/messages-container.component";
import { AiMessage, AiService, Conversation } from '../services/ai.service';
import { lastValueFrom } from 'rxjs';
import { Tooltip } from "primeng/tooltip";
import { OnboardingPendingService } from '../dashboardV2/onboarding-pending.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AiAssistantUiService, isNovaChatPage } from '../services/ai-assistant-ui.service';
import { stripPageContext } from '../ai-chatbot/attachment-markers';

const PRODUCT_RISK_SUGGESTION = "What are the top 20 risky items that are clearly related to ingredients or products? Provide the direct link and a short description of the potential risk for each link.";
const TIKTOK_SHOP_PRODUCT_RISK_SUGGESTION = "Which 20 TikTok Shop products have the highest compliance risk from product or ingredient claims? For each, give the product name, the flagged wording, and a brief explanation.";

@Component({
  selector: 'app-ai-assistant',
  imports: [PopoverModule, NovaWorkComponent, CommonModule, MessagesContainerComponent, Tooltip],
  templateUrl: './ai-assistant.component.html',
  styleUrl: './ai-assistant.component.scss',
})
export class AiAssistantComponent implements OnInit {
  readonly activity = inject(NovaActivityService);
  assistantView: 'chat' | 'work' = 'chat';
  openingWorkConversationId: string | null = null;
  @ViewChild(MessagesContainerComponent) private chatMessages?: MessagesContainerComponent;
  assistantOpen = false;
  suggestions: string[] = []
  router = inject(Router)
  route = inject(ActivatedRoute);
  @ViewChild('op') popover!: Popover;
  @ViewChild('aiToggleBtn') aiToggleBtn!: ElementRef;
  @ViewChild('aiPopoverAnchor') aiPopoverAnchor!: ElementRef;
  dashboardSuggestions = [
    "Create an exact URL website scan project for me",
    "What types of scans can I do?",
    "What can I do when I face a scan error?",
  ]
  keywordListSuggestions = [
    "Verify all my keyword lists and identify the same keyword in multiple lists",
    "How can I create a new keyword list?",
    "Give me tips to create a new keyword list",
    "Can I share a keyword list with my team?"
  ]
  keywordSuggestions = [
    "Create a new keyword by retrieving all my keywords and consulting the Vector Database Tool for relevant terms",
    "Verify all my keywords; if there are duplicates, keep just one, merging their information",
    "How can I create a new keyword?"
  ]
  websiteReportSuggestions = [
    "Remove false positives",
    PRODUCT_RISK_SUGGESTION
  ]
  productReportSuggestions = [
    PRODUCT_RISK_SUGGESTION
  ]
  tiktokShopReportSuggestions = [
    TIKTOK_SHOP_PRODUCT_RISK_SUGGESTION
  ]
  reportSuggestions = [
    "What are some risky items related to products or ingredients in this report?"
  ]
  conversation!: Conversation | null;
  initialMessages: AiMessage[] = [];
  pendingInitialPrompt: string | null = null;
  attributes!: any;
  aiService = inject(AiService)
  pendingService = inject(OnboardingPendingService)
  uiService = inject(AiAssistantUiService)
  private readonly destroyRef = inject(DestroyRef);
  previousUrl!: string
  isSelectConversationLoading = false;
  private waitingConversationProjectId: string | null = null;
  private conversationLoadVersion = 0;
  dragPosition = { x: 0, y: 0 };
  private isDragging = false;
  private dragStarted = false;
  private hasDragged = false;
  private dragPointerId: number | null = null;
  private dragStartPointer = { x: 0, y: 0 };
  private dragOffset = { x: 0, y: 0 };
  private readonly dragThreshold = 4;

  ngOnInit(): void {
    this.setDefaultFloatingPosition();
    // Load context of the current page on mount
    this.getConversationDetails(this.router.url);
    this.listenToConversationReady();
    this.listenToUiCommands();
    this.activity.workRequested$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.openWork());
    this.activity.scopeChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.conversationLoadVersion++;
      this.openingWorkConversationId = null;
      this.popover?.hide();
      this.conversation = null;
      this.initialMessages = [];
      this.attributes = null;
      this.pendingInitialPrompt = null;
      this.waitingConversationProjectId = null;
      this.isSelectConversationLoading = false;
    });
    this.activity.openConversation$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(chat => {
      if (isNovaChatPage(this.router.url)) {this.popover?.hide(); return;}
      this.conversationLoadVersion++;
      this.waitingConversationProjectId = null;
      this.isSelectConversationLoading = false;
      this.pendingInitialPrompt = null;
      const sameConversation = this.conversation?.id === chat.id;
      const ready = sameConversation && !!this.chatMessages?.messages.length && !this.chatMessages.isConversationLoading;
      this.openingWorkConversationId = ready ? null : chat.id;
      if (!ready) this.initialMessages = [];
      if (this.conversation?.id !== chat.id) {
        this.attributes = chat.attributes?.projectId ? {projectId: chat.attributes.projectId,
          reportType: chat.attributes.reportType, postId: chat.attributes.postId,
          isMediaLegacy: chat.attributes.isMediaLegacy} : null;
        this.conversation = chat;
      }
      if (ready) this.showAssistantChat();
      else this.assistantView = 'work';
      this.openAssistantPopover();
      if (sameConversation && !ready) void this.chatMessages?.selectConversation();
    });

    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const previousNavigation = this.router.getCurrentNavigation()?.previousNavigation;
        if (previousNavigation && previousNavigation?.finalUrl) this.previousUrl = previousNavigation?.finalUrl?.toString();

        this.setDefaultFloatingPosition();
        this.getConversationDetails(event.url);
        this.updatePopoverVisibility(event.url);
      }
    });
  }

  private setDefaultFloatingPosition(): void {
    const btnSize = 64;
    const gap = 32;
    this.dragPosition = {
      x: window.innerWidth - btnSize - gap,
      y: window.innerHeight - btnSize - gap,
    };
    this.clampDragPosition();
  }

  onDragStart(event: PointerEvent): void {
    if (event.button !== 0) return;
    this.dragPointerId = event.pointerId;
    this.dragStartPointer = { x: event.clientX, y: event.clientY };
    this.dragOffset = {
      x: event.clientX - this.dragPosition.x,
      y: event.clientY - this.dragPosition.y,
    };
    this.dragStarted = false;
    this.isDragging = true;
  }

  onToggleClick(event: MouseEvent): void {
    if (this.hasDragged) {
      this.hasDragged = false;
      return;
    }
    if (!this.assistantOpen && (this.activity.active().length || this.activity.unreadCount())) this.assistantView = 'work';
    this.toggleAssistantPopover();
  }

  toggleAssistantPopover(): void {
    if (isNovaChatPage(this.router.url)) {this.popover?.hide(); return;}
    if (!this.popover || !this.aiPopoverAnchor?.nativeElement) return;
    const isVisible = (this.popover as any).overlayVisible ?? false;
    if (isVisible) {
      this.popover.hide();
      return;
    }
    this.popover.show(null, this.aiPopoverAnchor.nativeElement);
  }

  openWork(): void {
    this.openingWorkConversationId = null;
    this.assistantView = 'work';
    this.openAssistantPopover();
  }

  onAssistantHide(): void {
    this.assistantOpen = false;
  }

  showAssistantChat(): void {
    this.openingWorkConversationId = null;
    this.assistantView = 'chat';
  }

  onConversationLoaded(result: ConversationLoadResult): void {
    if (this.openingWorkConversationId !== result.conversationId) return;
    this.openingWorkConversationId = null;
    if (result.success) this.showAssistantChat();
  }

  @HostListener('document:pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (!this.isDragging || this.dragPointerId !== event.pointerId) return;

    const movedX = Math.abs(event.clientX - this.dragStartPointer.x);
    const movedY = Math.abs(event.clientY - this.dragStartPointer.y);
    if (!this.dragStarted && (movedX > this.dragThreshold || movedY > this.dragThreshold)) {
      this.dragStarted = true;
      this.hasDragged = true;
    }
    if (!this.dragStarted) return;

    this.dragPosition = {
      x: event.clientX - this.dragOffset.x,
      y: event.clientY - this.dragOffset.y,
    };
    this.clampDragPosition();
  }

  @HostListener('document:pointerup', ['$event'])
  onPointerUp(event: PointerEvent): void {
    if (this.dragPointerId !== event.pointerId) return;
    this.isDragging = false;
    this.dragStarted = false;
    this.dragPointerId = null;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.clampDragPosition();
  }

  private clampDragPosition(): void {
    const element = this.aiToggleBtn?.nativeElement as HTMLElement | undefined;
    const width = element?.offsetWidth || 64;
    const height = element?.offsetHeight || 64;
    const maxX = Math.max(0, window.innerWidth - width);
    const maxY = Math.max(0, window.innerHeight - height);

    this.dragPosition = {
      x: Math.min(Math.max(0, this.dragPosition.x), maxX),
      y: Math.min(Math.max(0, this.dragPosition.y), maxY),
    };
  }

  private getReportSuggestions(reportType: string | null | undefined): string[] {
    if (reportType === 'website') return this.websiteReportSuggestions
    if (reportType === 'tiktok-shop') return this.tiktokShopReportSuggestions
    if (reportType === 'meta' || reportType === 'amazon') return this.productReportSuggestions
    return this.reportSuggestions
  }

  private routeSegmentAfter(url: string, marker: string): string | undefined {
    return url.split(marker)[1]?.split(/[/?#]/)[0] || undefined;
  }

  private listenToConversationReady(): void {
    this.pendingService.conversationReady$.subscribe(async ({ projectId, reportType }) => {
      if (reportType !== 'website') return;
      if (this.waitingConversationProjectId !== projectId) return;

      this.openAssistantPopover();
      const hasBootstrap = this.applyPendingConversationBootstrap(projectId);
      if (hasBootstrap) {
        this.isSelectConversationLoading = false;
        this.waitingConversationProjectId = null;
        return;
      }

      await this.loadLatestConversationForProject(projectId);
      this.isSelectConversationLoading = false;
      this.waitingConversationProjectId = null;
    });

    this.pendingService.conversationPending$.subscribe((isPending) => {
      if (isPending || !this.waitingConversationProjectId) return;
      this.isSelectConversationLoading = false;
    });
  }

  openAssistantPopover(): void {
    if (isNovaChatPage(this.router.url)) {this.popover?.hide(); return;}
    if (!this.popover || !this.aiPopoverAnchor?.nativeElement) return;
    if (this.popover.overlayVisible) return;
    this.popover.show(null, this.aiPopoverAnchor.nativeElement);
  }

  private listenToUiCommands(): void {
    this.uiService.commands$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((cmd) => {
        if (isNovaChatPage(this.router.url)) {this.popover?.hide(); return;}
        if (cmd.type === 'open') {
          this.showAssistantChat();
          if (cmd.prompt) this.pendingInitialPrompt = cmd.prompt;
          this.openAssistantPopover();
          return;
        }
        if (cmd.type === 'toggle') {
          this.toggleAssistantPopover();
          return;
        }
        if (cmd.type === 'close') {
          this.popover?.hide();
        }
      });
  }

  private updatePopoverVisibility(url: string): void {
    if (!this.popover) return;
    const isScanProject = url.includes('/dashboard/website-scan') && !url.includes('/dashboard/website-scan/legacy')
      || url.includes('/dashboard/social-media-scan/page')
      || url.includes('/dashboard/media-scan')
      || url.includes('/dashboard/document-scan')
      || url.includes('/dashboard/amazon-scan')
      || url.includes('/dashboard/tiktok-shop-scan');
    if (!isScanProject) this.popover.hide();
  }

  async getConversationDetails(url: string) {
    this.conversationLoadVersion++;
    this.attributes = null

    // Keyword list
    if(url.includes('/keywords/keyword-list')) this.suggestions = this.keywordListSuggestions

    //Dashboard
    else if(url.endsWith('/dashboard')) this.suggestions = this.dashboardSuggestions

    // Website scan
    else if(url.includes('/dashboard/website-scan')) {
      const projectId = this.routeSegmentAfter(url, 'website-scan/')
      this.suggestions = this.getReportSuggestions('website')
      this.attributes = projectId ? { projectId, reportType: 'website' } : null

      if (projectId && this.applyPendingConversationBootstrap(projectId)) {
        this.isSelectConversationLoading = false;
        this.waitingConversationProjectId = null;
        return;
      }

      const shouldWaitConversation = !!projectId && this.pendingService.conversationPending;
      if (shouldWaitConversation) {
        this.waitingConversationProjectId = projectId;
        this.isSelectConversationLoading = true;
        this.conversation = null;
        this.initialMessages = [];
        this.pendingInitialPrompt = null;
        this.openAssistantPopover();
      }

      const hasConversation = await this.loadLatestConversationForProject(projectId);
      if (!hasConversation && shouldWaitConversation) {
        this.openAssistantPopover();
      } else {
        this.waitingConversationProjectId = null;
        this.isSelectConversationLoading = false;
      }
    }

    else if(url.includes('/dashboard/social-media-scan/page')) {
      const projectId = this.routeSegmentAfter(url, 'social-media-scan/page/')
      this.suggestions = this.getReportSuggestions('meta')
      this.attributes = projectId
        ? url.includes('/post/')
          ? { projectId, postId: this.routeSegmentAfter(url, 'post/'), reportType: 'meta' }
          : { projectId, reportType: 'meta' }
        : null

      if (this.previousUrl?.includes('/dashboard/social-media-scan/page') && this.conversation?.attributes?.projectId === projectId && this.conversation?.attributes?.reportType === 'meta') return;
      await this.loadLatestConversationForProject(projectId);
    }

    // Media Scan
    else if(url.includes('/dashboard/media-scan')) {
      const isMediaLegacy = this.route.snapshot.queryParamMap.get('isMediaLegacy') === 'true' ;
      const projectId = url.includes('/media-scan/single-file/')
        ? this.routeSegmentAfter(url, 'media-scan/single-file/')
        : this.routeSegmentAfter(url, 'media-scan/')

      if ((url.includes('/media-scan/single-file') || url.includes('/transcript/')) &&
          !this.previousUrl?.includes('/dashboard/media-scan')) {
        this.resetConversation();
      }

      this.suggestions = this.getReportSuggestions('media')
      this.attributes = projectId ? { projectId, reportType: 'media', isMediaLegacy } : null

      if (this.previousUrl?.includes('/dashboard/media-scan') && this.conversation?.attributes?.projectId === projectId && this.conversation?.attributes?.reportType === 'media') return

      await this.loadLatestConversationForProject(projectId)
    }

    // Document scan
    else if(url.includes('/dashboard/document-scan')) {
      const docScan = this.extractDocumentScanAttributes(url)
      this.suggestions = docScan ? this.getReportSuggestions(docScan.reportType) : this.suggestions
      this.attributes = docScan ? { ...docScan } : null

      if (!docScan || (this.previousUrl?.includes('/dashboard/document-scan') && this.conversation?.attributes?.projectId === docScan.projectId && this.conversation?.attributes?.reportType === docScan.reportType)) return

      await this.loadLatestConversationForProject(docScan.projectId)
    }
    
    // Amazon scan
    else if(url.includes('/dashboard/amazon-scan')) {
      this.suggestions = this.getReportSuggestions('amazon')
      const amazonAttributes = this.extractAmazonScanAttributes(url)
      this.attributes = amazonAttributes

      if (this.previousUrl?.includes('/dashboard/amazon-scan') && this.conversation?.attributes?.projectId === amazonAttributes?.projectId && this.conversation?.attributes?.reportType === 'amazon') return

      await this.loadLatestConversationForProject(amazonAttributes?.projectId)
    }

    // TikTok Shop scan
    else if(url.includes('/dashboard/tiktok-shop-scan')) {
      this.suggestions = this.getReportSuggestions('tiktok-shop')
      const tiktokShopAttributes = this.extractTikTokShopScanAttributes(url)
      this.attributes = tiktokShopAttributes

      if (this.previousUrl?.includes('/dashboard/tiktok-shop-scan') && this.conversation?.attributes?.projectId === tiktokShopAttributes?.projectId && this.conversation?.attributes?.reportType === 'tiktok-shop') return

      await this.loadLatestConversationForProject(tiktokShopAttributes?.projectId)
    }

    //Keywords
    else if(url.includes('/keywords/all-keywords')) this.suggestions = this.keywordSuggestions

    // Reset chat if user exits scan project page
    const isCurrentScanPage = url.includes('/dashboard/website-scan')
      || url.includes('/dashboard/social-media-scan/page')
      || url.includes('/dashboard/document-scan')
      || url.includes('/dashboard/media-scan')
      || url.includes('/dashboard/amazon-scan')
      || url.includes('/dashboard/tiktok-shop-scan');
    const wasPreviousScanPage = this.previousUrl?.includes('/dashboard/website-scan')
      || this.previousUrl?.includes('/dashboard/social-media-scan/page')
      || this.previousUrl?.includes('/dashboard/document-scan')
      || this.previousUrl?.includes('/dashboard/media-scan')
      || this.previousUrl?.includes('/dashboard/amazon-scan')
      || this.previousUrl?.includes('/dashboard/tiktok-shop-scan');

    if (wasPreviousScanPage && !isCurrentScanPage) {
      this.conversation = null
      this.pendingInitialPrompt = null;
      this.isSelectConversationLoading = false;
      this.waitingConversationProjectId = null;
    }
  }

  private applyPendingConversationBootstrap(projectId: string): boolean {
    const bootstrap = this.pendingService.consumePendingConversationBootstrap(projectId);
    if (!bootstrap) return false;

    this.conversation = bootstrap.conversation;
    this.initialMessages = [];
    this.pendingInitialPrompt = bootstrap.prompt;
    this.openAssistantPopover();
    return true;
  }

  private async loadLatestConversationForProject(projectId: string | undefined): Promise<boolean> {
    const loadVersion = ++this.conversationLoadVersion;
    if (this.conversation && (this.conversation.attributes?.projectId !== projectId || this.conversation.attributes?.reportType !== this.attributes?.reportType)) {
      this.conversation = null;
      this.initialMessages = [];
    }
    const allConversations = await lastValueFrom(this.aiService.getChatbotConversations())
    if (loadVersion !== this.conversationLoadVersion) return false;
    const conversation = allConversations
      .filter((x) => x?.attributes?.projectId === projectId)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .at(0);
    if (conversation?.id) {
      const res = await lastValueFrom(this.aiService.getChatbotMessages(conversation.id));
      if (loadVersion !== this.conversationLoadVersion) return false;
      // Scan-analysis conversations open with a system-crafted seed turn + the raw
      // JSON summary. Hide those so "Discuss with NovaAI" reads as a clean thread;
      // if nothing else is there yet, show a friendly opener instead of an empty chat.
      const isScanChat = res.messages.some((m) => this.isScanAnalysisArtifact(m));
      const visible = res.messages.filter((m) => !this.isScanAnalysisArtifact(m));
      if (isScanChat && !visible.some((m) => m.role === 'assistant')) {
        visible.unshift({
          id: 'scan-analysis-intro',
          role: 'assistant',
          content:
            "I've reviewed your scan and prepared the summary on the report. Answer my questions above, or ask me anything about the findings — I can rule out false positives and refine the analysis.",
          createdAt: conversation.createdAt,
        });
      }
      this.initialMessages = visible;
    } else {
      this.initialMessages = [];
    }
    this.pendingInitialPrompt = null;
    this.conversation = conversation ?? null;
    return !!conversation?.id;
  }

  /** True for the technical seed turn or the raw JSON summary in a scan chat. */
  private isScanAnalysisArtifact(m: AiMessage): boolean {
    // The backend prepends the page-context block to every sent turn, the seeded
    // scan-analysis turn included — strip it before matching on the lead-in.
    const c = stripPageContext((m.content ?? '').trim()).trim();
    if (c.startsWith('A single-page website compliance scan just finished')) return true;
    if (
      m.role === 'assistant' &&
      c.startsWith('{') &&
      c.includes('"summary"') &&
      c.includes('"relativeRisk"')
    ) {
      return true;
    }
    return false;
  }

  resetSuggestions() {
    this.suggestions = []
  }

  resetConversation() {
    this.showAssistantChat();
    this.conversation = null
    this.initialMessages = [];
    this.attributes = null;
    this.pendingInitialPrompt = null;
    this.refreshSuggestionsForCurrentPage()
  }

  /** Page-specific configurations*/
  private readonly pageSuggestionsConfig = [
    { matches: (url: string) => url.includes('/keywords/keyword-list'), suggestions: () => this.keywordListSuggestions },
    { matches: (url: string) => url.endsWith('/dashboard'), suggestions: () => this.dashboardSuggestions },
    {
      matches: (url: string) => url.includes('/dashboard/website-scan'),
      suggestions: () => this.getReportSuggestions('website'),
      getAttributes: (url: string) => {
        const projectId = this.routeSegmentAfter(url, 'website-scan/')
        return projectId ? { projectId, reportType: 'website' } : null
      },
    },
    {
      matches: (url: string) => url.includes('/dashboard/social-media-scan/page'),
      suggestions: () => this.getReportSuggestions('meta'),
      getAttributes: (url: string) => {
        const projectId = this.routeSegmentAfter(url, 'social-media-scan/page/')
        if (!projectId) return null
        if (url.includes('/post/')) {
          const postId = this.routeSegmentAfter(url, 'post/')
          return { projectId, postId, reportType: 'meta' }
        }
        return { projectId, reportType: 'meta' }
      },
    },
    {
      matches: (url: string) => url.includes('/dashboard/media-scan'),
      suggestions: () => this.getReportSuggestions('media'),
      getAttributes: (url: string) => {
        const isMediaLegacy = this.route.snapshot.queryParamMap.get('isMediaLegacy') === 'true'
        const projectId = url.includes('/media-scan/single-file/')
          ? this.routeSegmentAfter(url, 'media-scan/single-file/')
          : this.routeSegmentAfter(url, 'media-scan/')
        if (!projectId) return null
        return { projectId, reportType: 'media', isMediaLegacy }
      },
    },
    {
      matches: (url: string) => url.includes('/dashboard/document-scan'),
      suggestions: () => this.reportSuggestions,
      getAttributes: (url: string) => this.extractDocumentScanAttributes(url),
    },
    {
      matches: (url: string) => url.includes('/dashboard/amazon-scan'),
      suggestions: () => this.getReportSuggestions('amazon'),
      getAttributes: (url: string) => this.extractAmazonScanAttributes(url),
    },
    {
      matches: (url: string) => url.includes('/dashboard/tiktok-shop-scan'),
      suggestions: () => this.getReportSuggestions('tiktok-shop'),
      getAttributes: (url: string) => this.extractTikTokShopScanAttributes(url),
    },
    { matches: (url: string) => url.includes('/keywords/all-keywords'), suggestions: () => this.keywordSuggestions },
  ] as const

  /** Restore suggestions for the current page */
  private refreshSuggestionsForCurrentPage(): void {
    const url = this.router.url
    const match = this.pageSuggestionsConfig.find((c) => c.matches(url))

    if (!match) {
      this.suggestions = []
      return
    }

    this.suggestions = match.suggestions()

    if ('getAttributes' in match && match.getAttributes) {
      const attrs = match.getAttributes(url)
      if (!attrs) {
        if (url.includes('/dashboard/document-scan')) this.suggestions = []
        return
      }
      this.attributes = attrs
    }
  }

  private extractDocumentScanAttributes(url: string): { projectId: string; reportType: string; realProjectId: string } | null {
    const pathAfterDocumentScan = url.split('/dashboard/document-scan/')[1]
    if (!pathAfterDocumentScan) return null

    const segments = pathAfterDocumentScan.split('/')
    const routeType = segments[0]
    const urlParam = segments[1]

    if (!urlParam || ['transcript', 'transcript-office', 'docx-info', 'xlsx-info'].includes(urlParam)) return null

    if (routeType === 'single-document') {
      const transcriptIndex = segments.findIndex(s => s === 'transcript' || s === 'transcript-office')
      const encodedPathRaw = transcriptIndex > 1
        ? segments.slice(1, transcriptIndex).join('/')
        : urlParam
      const encodedPath = (encodedPathRaw as string).split('?')[0]
      const realProjectId = this.decodeSingleDocumentRealProjectId(encodedPath)
      return {
        projectId: encodedPath,
        reportType: 'single-document',
        realProjectId: realProjectId ?? encodedPath
      }
    }

    const projectId = urlParam.split('?')[0]
    return { projectId, reportType: routeType, realProjectId: projectId }
  }

  private extractAmazonScanAttributes(url: string): { projectId: string; reportType: string; scanMarketplaceId?: string; itemId?: string } | null {
    const projectId = this.routeSegmentAfter(url, 'amazon-scan/')
    if (!projectId) return null

    const attributes: { projectId: string; reportType: string; scanMarketplaceId?: string; itemId?: string } = {
      projectId,
      reportType: 'amazon',
    }

    if (url.includes('/scan-marketplace/')) {
      const scanMarketplaceId = this.routeSegmentAfter(url, '/scan-marketplace/')
      if (scanMarketplaceId) attributes.scanMarketplaceId = scanMarketplaceId
    }

    if (url.includes('/transcript/')) {
      const itemId = this.routeSegmentAfter(url, '/transcript/')
      if (itemId) attributes.itemId = itemId
    }

    return attributes
  }

  private extractTikTokShopScanAttributes(url: string): { projectId: string; reportType: string; productId?: string } | null {
    const projectId = this.routeSegmentAfter(url, 'tiktok-shop-scan/')
    if (!projectId) return null

    const attributes: { projectId: string; reportType: string; productId?: string } = {
      projectId,
      reportType: 'tiktok-shop',
    }

    if (url.includes('/transcript/')) {
      const productId = this.routeSegmentAfter(url, '/transcript/')
      if (productId) attributes.productId = productId
    }

    return attributes
  }

  /** Decodes base64 path "projectId/filePath" and returns the real project ID (first segment). */
  private decodeSingleDocumentRealProjectId(encodedPath: string): string | null {
    const tryDecode = (raw: string): string | null => {
      try {
        const decoded = atob(raw)
        const first = decoded.split('/')[0]
        return first && first.length > 0 ? first : null
      } catch {
        return null
      }
    }
    const decoded = tryDecode(decodeURIComponent(encodedPath)) ?? tryDecode(encodedPath)
    return decoded
  }

  selectConversation(conversation: Conversation) {
    this.conversation = conversation
  }

  hasAccessToAiAssistant(): boolean {
    const { url } = this.router;
    if (isNovaChatPage(url)) return false;

    const isDashboardRoot = url.endsWith('/dashboard');
    const isKeywordsSection = url.includes('/keywords');

    const isWebsiteScan = url.includes('/dashboard/website-scan');
    const isLegacyWebsiteScan = url.includes('/dashboard/website-scan/legacy');

    const isSocialMedia = url.includes('/dashboard/social-media-scan/page');

    const isMediaScan = url.includes('/dashboard/media-scan');
    const isDocumentScan = url.includes('/dashboard/document-scan');
    const isAmazonScan = url.includes('/dashboard/amazon-scan');
    const isTikTokShopScan = url.includes('/dashboard/tiktok-shop-scan');

    const isModernWebsiteScan = isWebsiteScan && !isLegacyWebsiteScan;

    return isDashboardRoot || isKeywordsSection || isModernWebsiteScan || isSocialMedia || isMediaScan || isDocumentScan || isAmazonScan || isTikTokShopScan || this.activity.active().length > 0 || this.activity.unreadCount() > 0;
  }
}
